import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AdminOrderService } from '../../../services/admin-order.service';
import { PdfExportService } from '../../../services/pdf-export.service';
import { ProductLocalizationService } from '../../../services/product-localization.service';
import { ToastService } from '../../../services/toast.service';
import { ShiprocketService } from '../../../services/shiprocket.service';
import { AdminOrder } from '../order-management/order-management.component';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { Firestore, doc, updateDoc } from '@angular/fire/firestore';

@Component({
  selector: 'app-order-detail',
  templateUrl: './order-detail.component.html',
  styleUrls: ['./order-detail.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModalModule]
})
export class OrderDetailComponent implements OnInit, OnDestroy {
  order: AdminOrder | null = null;
  originalOrder: AdminOrder | null = null;
  loading = false;
  error: string | null = null;
  estimatedDeliveryDate: string = '';
  exporting = false;
  showImageModal = false;
  selectedImage: string = '';
  shiprocketTrackingData: any = null; // Store Shiprocket tracking data
  
  // Accordion state
  accordionState: { [key: string]: boolean } = {
    orderInfo: true,
    customerInfo: true,
    orderStatus: true,
    shippingInfo: true,
    shiprocketInfo: true,
    products: false,
    orderHistory: false,
    notes: false
  };
  
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private adminOrderService: AdminOrderService,
    private pdfExportService: PdfExportService,
    private productLocalizationService: ProductLocalizationService,
    private toastService: ToastService,
    private modalService: NgbModal,
    private shiprocketService: ShiprocketService,
    private firestore: Firestore
  ) {}

  ngOnInit(): void {
    this.loadOrder();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadOrder(): Promise<void> {
    try {
      this.loading = true;
      this.error = null;
      
      const orderId = this.route.snapshot.paramMap.get('id');
      if (!orderId) {
        this.error = 'Order ID not provided';
        return;
      }

      // Load order details
      const result = await this.adminOrderService.getAdminOrders(1000).toPromise();
      if (result) {
        const foundOrder = result.orders.find((o: any) => o.id === orderId);
        if (foundOrder) {
          let loadedOrder = { ...foundOrder };
          
          // Enrich products with i18n data from database
          if (loadedOrder.products && Array.isArray(loadedOrder.products)) {
            loadedOrder.products = await Promise.all(
              loadedOrder.products.map(product => this.productLocalizationService.enrichProductWithI18n(product))
            );
          }
          
          this.order = loadedOrder; // Shiprocket fields are already included from getAdminOrders
          this.originalOrder = { ...this.order };
          
          // Set estimated delivery date for the date input
          if (this.order.estimatedDelivery) {
            const date = this.order.estimatedDelivery.seconds ? 
              new Date(this.order.estimatedDelivery.seconds * 1000) : 
              new Date(this.order.estimatedDelivery);
            this.estimatedDeliveryDate = date.toISOString().split('T')[0];
          }
          
          // Load Shiprocket tracking data if shipment exists
          if (this.order.shiprocketAwbCode) {
            await this.loadShiprocketTracking();
          }
          
          this.toastService.success(`Order ${this.order.id} loaded successfully`);
        } else {
          this.error = 'Order not found';
          this.toastService.error('Order not found');
        }
      }
    } catch (error) {
      this.error = 'Failed to load order details';
    } finally {
      this.loading = false;
    }
  }  

  goBack(): void {
    this.router.navigate(['/admin/orders']);
  }

  async exportOrder(): Promise<void> {
    if (!this.order) return;
    
    try {
      this.exporting = true;
      await this.pdfExportService.exportOrderToPDF(this.order);
      this.toastService.success(`Order ${this.order.id} exported successfully!`);
    } catch (error) {
      console.error('Error exporting order:', error);
      this.toastService.error('Failed to export order. Please try again.');
    } finally {
      this.exporting = false;
    }
  }

  hasChanges(): boolean {
    if (!this.order || !this.originalOrder) return false;
    
    return JSON.stringify(this.order) !== JSON.stringify(this.originalOrder);
  }

  resetChanges(): void {
    if (this.originalOrder) {
      this.order = { ...this.originalOrder };
      
      // Reset estimated delivery date
      if (this.order.estimatedDelivery) {
        const date = this.order.estimatedDelivery.seconds ? 
          new Date(this.order.estimatedDelivery.seconds * 1000) : 
          new Date(this.order.estimatedDelivery);
        this.estimatedDeliveryDate = date.toISOString().split('T')[0];
      }
      
      this.toastService.info('Changes reset to original values');
    }
  }

  onStatusChange(newStatus: string): void {
    if (!this.order) return;
    
    const now = new Date();
    // Capture the previous status BEFORE updating (ngModelChange fires before binding updates)
    // Use originalOrder.status if available, otherwise use current order.status
    const previousStatus = this.originalOrder?.status || this.order.status || newStatus;
    
    console.log('=== onStatusChange() called ===');
    console.log('New status:', newStatus);
    console.log('Current order.status:', this.order.status);
    console.log('originalOrder?.status:', this.originalOrder?.status);
    console.log('Previous status (calculated):', previousStatus);
    
    // Update the status (will be reverted if user cancels confirmation)
    // Type assertion is safe because dropdown only has valid status values
    this.order.status = newStatus as 'pending' | 'confirmed' | 'shipped' | 'delivered' | 'cancelled';
    
    // VALIDATION: Cannot revert from shipped to pending/confirmed/cancelled
    if (previousStatus === 'shipped' && 
        (this.order.status === 'pending' || 
         this.order.status === 'confirmed' || 
         this.order.status === 'cancelled')) {
      this.toastService.error('Cannot revert shipped order. Once shipped, status cannot be changed.');
      this.order.status = previousStatus; // Revert to shipped
      return;
    }
    
    // VALIDATION: Cannot mark as delivered from dropdown (only Shiprocket can do this)
    if (this.order.status === 'delivered') {
      this.toastService.warning('Delivered status can only be set by Shiprocket tracking.');
      this.order.status = previousStatus; // Revert
      return;
    }
    
    // VALIDATION: Cannot cancel if already shipped
    if (previousStatus === 'shipped' && this.order.status === 'cancelled') {
      this.toastService.error('Cannot cancel order that is already shipped.');
      this.order.status = previousStatus; // Revert to shipped
      return;
    }
    
    // Show confirmation for shipping status (cannot be undone)
    if (this.order.status === 'shipped' && previousStatus !== 'shipped') {
      if (!confirm('Mark this order as shipped? This action cannot be undone. Once shipped, the status cannot be reverted.')) {
        this.order.status = previousStatus; // Revert to previous status
        return;
      }
    }
    
    // Show confirmation for cancellation (only available before shipping)
    if (this.order.status === 'cancelled' && previousStatus !== 'cancelled') {
      if (!confirm('Are you sure you want to cancel this order? This action cannot be undone.')) {
        this.order.status = previousStatus; // Revert to previous status
        return;
      }
    }
    
    // Automatically set dates based on status changes
    switch (this.order.status) {
      case 'confirmed':
        // Set confirmation date if not already set
        if (!this.order.confirmedDate) {
          this.order.confirmedDate = now;
        }
        this.toastService.info('Order confirmed. Customer will be notified.');
        break;
      case 'shipped':
        // Set shipping date
        this.order.shippingDate = now;
        // Set shipping provider to Shiprocket
        this.order.shippingProvider = 'shiprocket';
        // Set estimated delivery to 3-5 days from now if not set
        if (!this.estimatedDeliveryDate) {
          const estimatedDate = new Date(now);
          estimatedDate.setDate(estimatedDate.getDate() + 4); // 4 days from now
          this.estimatedDeliveryDate = estimatedDate.toISOString().split('T')[0];
        }
        // Check if shipment already exists
        // Note: AWB code may be null initially (assigned after courier selection), so check shipmentId only
        const hasValidShipment = !!this.order.shiprocketShipmentId;
        
        if (hasValidShipment) {
          // If shipment exists, set tracking number if AWB is available
          if (this.order.shiprocketAwbCode) {
            this.order.trackingNumber = this.order.shiprocketAwbCode;
            this.toastService.success('Order marked as shipped. Shiprocket tracking information available.');
          } else {
            this.toastService.info('Order marked as shipped. Shiprocket shipment created. AWB will be assigned after courier selection.');
          }
        } else {
          // Shipment doesn't exist - will be created in saveStatusChange() method
          this.toastService.info('Order marked as shipped. Creating Shiprocket shipment...');
        }
        // Show warning that status cannot be reverted
        this.toastService.warning('Order shipped. Status cannot be reverted.');
        break;
      case 'cancelled':
        this.toastService.warning('Order cancelled. Customer will be notified.');
        break;
    }
    
    // DO NOT update originalOrder.status here - it will be updated in saveStatusChange() after successful save
    // This ensures previousStatus check works correctly
    
    // Auto-save the status change to database
    this.saveStatusChange();
  }

  async saveStatusChange(): Promise<void> {
    if (!this.order) {
      console.error('saveStatusChange: No order found');
      return;
    }

    try {
      this.loading = true;
      
      // Store previous fields for rollback
      // IMPORTANT: Get previous status from originalOrder, not from current order
      // because order.status might have already been changed in onStatusChange()
      // If originalOrder doesn't exist, create a snapshot now to preserve the original state
      if (!this.originalOrder && this.order) {
        this.originalOrder = { ...this.order };
        console.log('originalOrder was null, created snapshot with status:', this.originalOrder.status);
      }
      const previousStatus = this.originalOrder?.status || this.order?.status || 'pending';
      const previousShippingDate = this.order.shippingDate;
      const previousEstimatedDelivery = this.estimatedDeliveryDate;
      const previousProvider = this.order.shippingProvider;
      
      console.log('=== saveStatusChange() called ===');
      console.log('Current status:', this.order.status);
      console.log('Previous status (from originalOrder):', previousStatus);
      console.log('originalOrder exists:', !!this.originalOrder);
      console.log('originalOrder.status:', this.originalOrder?.status);
      console.log('Has shipmentId:', !!this.order.shiprocketShipmentId);
      console.log('Has AWB:', !!this.order.shiprocketAwbCode);
      console.log('Condition check - status === shipped:', this.order.status === 'shipped');
      console.log('Condition check - previousStatus !== shipped:', previousStatus !== 'shipped');
      console.log('Will create shipment?', this.order.status === 'shipped' && previousStatus !== 'shipped');
      
      // Track if shipment was just created (to skip validation)
      let shipmentJustCreated = false;
      
      // If status is being changed to "shipped", create Shiprocket shipment FIRST
      if (this.order.status === 'shipped' && previousStatus !== 'shipped') {
        console.log('Status changed to shipped, checking for existing shipment...');
        // Check if shipment exists (AWB may be null initially - assigned after courier selection)
        const hasShipment = !!this.order.shiprocketShipmentId;
        console.log('Has shipment:', hasShipment);
        
        if (!hasShipment) {
          console.log('No shipment found, creating new shipment...');
          this.toastService.info('Creating Shiprocket shipment...');
          
          console.log('Calling createShiprocketShipment with orderId:', this.order.id);
          const result = await this.adminOrderService.createShiprocketShipment(
            this.order.id,
            this.order
          );
          console.log('createShiprocketShipment result:', result);
          
          if (!result.success) {
            // Rollback status to previous status
            this.order.status = previousStatus;
            if (this.originalOrder) {
              this.originalOrder.status = previousStatus;
            }
            // Revert UI fields mutated on shipped selection
            this.order.shippingDate = previousShippingDate;
            this.order.shippingProvider = previousProvider;
            this.estimatedDeliveryDate = previousEstimatedDelivery;
            
            // Show detailed error message
            const errorMsg = result.error || 'Failed to create shipment. Status not updated.';
            this.toastService.error(errorMsg);
            
            // If error mentions cancelled order, also show an alert with instructions
            if (errorMsg.includes('CANCELED') || errorMsg.includes('CANCELLED')) {
              setTimeout(() => {
                alert(errorMsg);
              }, 500);
            }
            return;
          }
          
          // Update order with Shiprocket details locally
          this.order.shiprocketShipmentId = result.shipmentId?.toString();
          this.order.shiprocketAwbCode = result.awbCode;
          this.order.shiprocketCourierName = result.courierName;
          this.order.trackingNumber = result.awbCode;
          this.order.shippingProvider = 'shiprocket';
          
          // Mark that shipment was just created
          shipmentJustCreated = true;
          
          if (result.awbCode) {
            this.toastService.success(`Shipment created successfully! AWB: ${result.awbCode}`);
          } else {
            this.toastService.success('Shipment created successfully! AWB will be assigned after courier validation.');
          }
          
          // IMPORTANT: Preserve the status before reloading, as loadOrder() will reset it from Firestore
          const statusToSave = this.order.status;
          
          // Wait a moment for Firestore to update, then reload order to ensure we have the latest shipment data
          await new Promise(resolve => setTimeout(resolve, 1000));
          await this.loadOrder();
          
          // Restore the status after reload (since Firestore hasn't been updated with new status yet)
          this.order.status = statusToSave;
        } else {
          console.log('Shipment already exists, skipping creation');
        }
      }
      
      // Now update order status (only if Shiprocket APIs succeeded)
      // Skip shipment validation if we just created it (to avoid race condition)
      console.log('Updating order status to:', this.order.status);
      console.log('Skip validation:', shipmentJustCreated);
      
      try {
        await this.adminOrderService.updateOrderStatus(
          this.order.id, 
          this.order.status, 
          undefined,
          undefined,
          shipmentJustCreated // Skip validation if shipment was just created
        );
        console.log('Order status updated successfully to:', this.order.status);
      } catch (error: any) {
        console.error('Error updating order status:', error);
        this.toastService.error('Failed to update order status: ' + (error?.message || 'Unknown error'));
        // Don't return - continue with other updates
      }

      // Update Shiprocket estimated delivery if provided
      if (this.order.status === 'shipped' && this.order.shiprocketAwbCode && this.estimatedDeliveryDate) {
        await this.adminOrderService.updateTrackingInfo(this.order.id, {
          trackingNumber: this.order.shiprocketAwbCode,
          shippingProvider: 'shiprocket',
          estimatedDelivery: new Date(this.estimatedDeliveryDate)
        });
      }
      
      // Ensure shipping provider is set to Shiprocket for shipped orders
      if (this.order.status === 'shipped' && !this.order.shippingProvider) {
        const orderRef = doc(this.firestore, 'orderTable', this.order.id);
        await updateDoc(orderRef, { shippingProvider: 'shiprocket' });
        this.order.shippingProvider = 'shiprocket';
      }

      // Update originalOrder to reflect saved state
      if (this.originalOrder) {
        this.originalOrder.status = this.order.status;
      }
      
      // Reload order to get latest data from database
      await this.loadOrder();
      
    } catch (error) {
      console.error('Error auto-saving status change:', error);
      this.toastService.error('Failed to save status change. Please try again.');
      // Revert status on error
      if (this.originalOrder) {
        this.order.status = this.originalOrder.status;
      }
      // Also revert UI fields potentially set for shipped
      await this.loadOrder();
    } finally {
      this.loading = false;
    }
  }

  async saveChanges(): Promise<void> {
    if (!this.order || !this.hasChanges()) return;

    try {
      this.loading = true;
      
      // Store previous status for rollback
      const previousStatus = this.originalOrder?.status || this.order.status;
      
      // If status is being changed to "shipped", create Shiprocket shipment FIRST
      if (this.order.status === 'shipped' && previousStatus !== 'shipped') {
        // If shipment doesn't exist, create it before updating status
        if (!this.order.shiprocketShipmentId) {
          this.toastService.info('Creating Shiprocket shipment...');
          const result = await this.adminOrderService.createShiprocketShipment(
            this.order.id,
            this.order
          );
          
          if (!result.success) {
            // Rollback status to previous status
            this.order.status = previousStatus;
            this.toastService.error(result.error || 'Failed to create shipment. Status not updated.');
            return;
          }
          
          // Update order with Shiprocket details
          this.order.shiprocketShipmentId = result.shipmentId?.toString();
          this.order.shiprocketAwbCode = result.awbCode;
          this.order.shiprocketCourierName = result.courierName;
          this.order.trackingNumber = result.awbCode;
          this.order.shippingProvider = 'shiprocket';
          this.toastService.success(`Shipment created successfully! AWB: ${result.awbCode}`);
        }
      }

      // Validate Shiprocket shipment exists for shipped orders
      if (this.order.status === 'shipped' && !this.order.shiprocketAwbCode) {
        this.toastService.warning('Please create Shiprocket shipment before marking order as shipped. Use "Create Shipment" button.');
        return;
      }

      // Now update order status (only if Shiprocket APIs succeeded)
      await this.adminOrderService.updateOrderStatus(
        this.order.id, 
        this.order.status, 
        this.order.notes
      );

      // Update Shiprocket estimated delivery if provided
      if (this.order.status === 'shipped' && this.order.shiprocketAwbCode && this.estimatedDeliveryDate) {
        await this.adminOrderService.updateTrackingInfo(this.order.id, {
          trackingNumber: this.order.shiprocketAwbCode,
          shippingProvider: 'shiprocket',
          estimatedDelivery: new Date(this.estimatedDeliveryDate)
        });
      }
      
      // Ensure shipping provider is set to Shiprocket for shipped orders
      if (this.order.status === 'shipped' && !this.order.shippingProvider) {
        const orderRef = doc(this.firestore, 'orderTable', this.order.id);
        await updateDoc(orderRef, { shippingProvider: 'shiprocket' });
        this.order.shippingProvider = 'shiprocket';
      }

      // Update local data
      this.originalOrder = { ...this.order };
      
      this.toastService.success('Order updated successfully');
      
    } catch (error) {
      console.error('Error updating order:', error);
      this.error = 'Failed to update order';
      // Rollback status on error
      if (this.originalOrder) {
        this.order.status = this.originalOrder.status;
      }
      this.toastService.error('Failed to update order. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'shipped': 'status-shipped',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled'
    };
    return statusClasses[status] || 'status-default';
  }

  getStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      'pending': 'Pending',
      'confirmed': 'Confirmed',
      'shipped': 'Shipped',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled'
    };
    return statusTexts[status] || status;
  }

  getPaymentStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'pending': 'payment-pending',
      'completed': 'payment-completed',
      'failed': 'payment-failed'
    };
    return statusClasses[status] || 'payment-default';
  }

  getPaymentStatusText(status: string): string {
    const statusTexts: { [key: string]: string } = {
      'pending': 'Pending',
      'completed': 'Completed',
      'failed': 'Failed'
    };
    return statusTexts[status] || status;
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    try {
      const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  formatDateFromString(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const dateObj = new Date(dateString);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  formatDateTime(date: any): string {
    if (!date) return '';
    try {
      const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  }

  getOrderTotal(order: AdminOrder): number {
    return order.totalAmount - (order.discountAmount || 0);
  }

  getProductAmount(order: AdminOrder): number {
    return order.totalAmount || 0;
  }

  getDeliveryCharges(order: AdminOrder): number {
    return order.deliveryCharges || 0;
  }

  getGrandTotal(order: AdminOrder): number {
    const productAmount = this.getProductAmount(order);
    const deliveryCharges = this.getDeliveryCharges(order);
    const discountAmount = order.discountAmount || 0;
    return productAmount + deliveryCharges - discountAmount;
  }

  getOrderHistory(): any[] {
    if (!this.order) return [];
    
    const history = [];
    
    if (this.order.orderDate) {
      history.push({
        date: this.order.orderDate,
        status: 'Order Placed',
        description: 'Order was placed by customer'
      });
    }
    
    if (this.order.confirmedDate) {
      history.push({
        date: this.order.confirmedDate,
        status: 'Order Confirmed',
        description: 'Order was confirmed by admin'
      });
    }
    
    if (this.order.shippingDate) {
      history.push({
        date: this.order.shippingDate,
        status: 'Order Shipped',
        description: 'Order was shipped to customer'
      });
    }
    
    if (this.order.deliveredDate) {
      history.push({
        date: this.order.deliveredDate,
        status: 'Order Delivered',
        description: 'Order was successfully delivered'
      });
    }
    
    return history.sort((a, b) => {
      const dateA = a.date.seconds ? new Date(a.date.seconds * 1000) : new Date(a.date);
      const dateB = b.date.seconds ? new Date(b.date.seconds * 1000) : new Date(b.date);
      return dateA.getTime() - dateB.getTime();
    });
  }

  getProductImage(product: any): string {
    // Try to get the actual product image
    if (product?.image) {
      const imageArray = Array.isArray(product.image) ? product.image : Object.values(product.image);
      if (imageArray && imageArray.length > 0) {
        const firstImage = imageArray[0];
        if (typeof firstImage === 'string') {
          // If it's a Firebase Storage URL or full URL, return it
          if (firstImage.startsWith('http') || firstImage.startsWith('https')) {
            return firstImage;
          }
          // If it's a local path, ensure it starts with ./
          if (firstImage.startsWith('./') || firstImage.startsWith('/product/')) {
            return firstImage.startsWith('./') ? firstImage : './' + firstImage.substring(1);
          }
        }
      }
    }
    
    // Fallback to dose-based placeholder image
    return this.getDoseImage(product?.doseType);
  }

  getDoseImage(doseType: string): string {
    switch (doseType?.toLowerCase()) {
      case 'dose one':
      case 'dose 1':
      case '1':
        return 'https://placehold.co/400x400/66BB6A/31343C?font=poppins&text=Dose+One';
      case 'dose two':
      case 'dose 2':
      case '2':
        return 'https://placehold.co/400x400/FFB74D/31343C?font=poppins&text=Dose+Two';
      case 'dose three':
      case 'dose 3':
      case '3':
        return 'https://placehold.co/400x400/D32F2F/31343C?font=poppins&text=Dose+Three';
      default:
        return 'https://placehold.co/400x400/9E9E9E/31343C?font=poppins&text=Product';
    }
  }

  openImageModal(imageUrl: string, content: any): void {
    this.selectedImage = imageUrl;
    this.modalService.open(content, { 
      size: 'lg',
      centered: true,
      backdrop: 'static'
    });
  }

  getGenderText(gender: string): string {
    switch (gender) {
      case 'male': return 'Male';
      case 'female': return 'Female';
      case 'other': return 'Other';
      default: return 'Not specified';
    }
  }

  formatWeight(weight: number): string {
    return weight > 0 ? `${weight} kg` : 'Not specified';
  }

  formatHeight(height: number): string {
    return height > 0 ? `${height} cm` : 'Not specified';
  }

  hasCustomerPhoto(): boolean {
    return !!(this.order?.userRecentPhoto || this.order?.userAvatar);
  }

  getCustomerPhotoUrl(): string {
    return this.order?.userRecentPhoto || this.order?.userAvatar || '';
  }

  // Localized product name
  getLocalizedName(product: any): string {
    return this.productLocalizationService.getLocalizedName(product);
  }

  // Localized product description
  getLocalizedDescription(product: any): string {
    return this.productLocalizationService.getLocalizedDescription(product);
  }

  // Get display name with category (e.g., "Height Amrut Dose-1 (Basic, Option A)")
  getDisplayNameWithCategory(product: any): string {
    if (!product) return '';
    const productName = this.getLocalizedName(product);
    const categoryName = product.selectedCategoryName;
    if (categoryName) {
      // Format category name
      const formattedCategoryName = categoryName
        .split(' ')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      
      // Add option label if sub-product option is selected
      if (product.selectedSubProductIndex !== undefined && product.selectedSubProductIndex >= 0) {
        const optionLabel = this.getOptionLabel(product.selectedSubProductIndex);
        return `${productName} (${formattedCategoryName}, ${optionLabel})`;
      }
      
      return `${productName} (${formattedCategoryName})`;
    }
    return productName;
  }

  // Get option label (Option A, Option B, etc.)
  getOptionLabel(index: number): string {
    return `Option ${String.fromCharCode(65 + index)}`;
  }

  // Get sub-products from the first product in the order - show selected option text
  getSubProducts(): string {
    if (!this.order?.products || this.order.products.length === 0) {
      return 'No items';
    }
    
    const product = this.order.products[0];
    
    // If selected sub-product text exists, use it (this is the actual data from DB)
    if (product.selectedSubProductText) {
      return product.selectedSubProductText;
    }
    
    // Fallback to array of sub-products (backward compatibility)
    const subProducts = product.selectedCategorySubProducts;
    if (subProducts) {
      // Check if it's an array
      if (Array.isArray(subProducts) && subProducts.length > 0) {
        return subProducts.join(', ');
      }
      // If it's a string, return it directly
      if (typeof subProducts === 'string' && subProducts.length > 0) {
        return subProducts;
      }
    }
    
    return 'No items';
  }

  /**
   * Create Shiprocket shipment for this order
   */
  async createShiprocketShipment(): Promise<void> {
    if (!this.order) return;

    try {
      this.loading = true;
      this.toastService.info('Creating Shiprocket shipment...');

      const result = await this.adminOrderService.createShiprocketShipment(this.order.id, this.order);

      if (result.success) {
        // Reload order to get updated Shiprocket data
        await this.loadOrder();
        this.toastService.success(`Shipment created successfully! AWB: ${result.awbCode}`);
      } else {
        this.toastService.error(result.error || 'Failed to create shipment');
      }
    } catch (error) {
      console.error('Error creating shipment:', error);
      this.toastService.error('Failed to create shipment. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Generate pickup request
   */
  async generatePickup(): Promise<void> {
    console.log('generatePickup() called');
    console.log('Order shipmentId:', this.order?.shiprocketShipmentId);
    
    if (!this.order?.shiprocketShipmentId) {
      this.toastService.warning('Shipment not found. Please create shipment first.');
      return;
    }

    try {
      this.loading = true;
      this.toastService.info('Requesting pickup...');
      
      const result = await this.shiprocketService.generatePickup(Number(this.order.shiprocketShipmentId)).toPromise();
      
      console.log('Pickup request result:', result);
      
      this.toastService.success('Pickup request generated successfully!');
    } catch (error: any) {
      console.error('Error generating pickup:', error);
      this.toastService.error('Failed to generate pickup request: ' + (error?.message || 'Unknown error'));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Generate manifest PDF
   */
  async generateManifest(): Promise<void> {
    console.log('generateManifest() called');
    console.log('Order shipmentId:', this.order?.shiprocketShipmentId);
    
    if (!this.order?.shiprocketShipmentId) {
      this.toastService.warning('Shipment not found. Please create shipment first.');
      return;
    }

    try {
      this.loading = true;
      this.toastService.info('Generating manifest...');
      
      const result = await this.shiprocketService.generateManifest(Number(this.order.shiprocketShipmentId)).toPromise();
      
      console.log('Manifest generation result:', result);
      
      if (result?.manifest_url) {
        // Update order with manifest URL
        const orderRef = doc(this.firestore, 'orderTable', this.order.id);
        await updateDoc(orderRef, { shiprocketManifestUrl: result.manifest_url });
        this.order.shiprocketManifestUrl = result.manifest_url;
        window.open(result.manifest_url, '_blank');
        this.toastService.success('Manifest generated successfully!');
      } else {
        this.toastService.error('Failed to generate manifest URL. Response: ' + JSON.stringify(result));
      }
    } catch (error: any) {
      console.error('Error generating manifest:', error);
      this.toastService.error('Failed to generate manifest: ' + (error?.message || 'Unknown error'));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Generate shipping label PDF
   */
  async generateLabel(): Promise<void> {
    console.log('generateLabel() called');
    console.log('Order shipmentId:', this.order?.shiprocketShipmentId);
    
    if (!this.order?.shiprocketShipmentId) {
      this.toastService.warning('Shipment not found. Please create shipment first.');
      return;
    }

    try {
      this.loading = true;
      this.toastService.info('Generating shipping label...');
      
      const result = await this.shiprocketService.generateLabel(Number(this.order.shiprocketShipmentId)).toPromise();
      
      console.log('Label generation result:', result);
      
      if (result?.label_url) {
        // Update order with label URL
        const orderRef = doc(this.firestore, 'orderTable', this.order.id);
        await updateDoc(orderRef, { shiprocketLabelUrl: result.label_url });
        this.order.shiprocketLabelUrl = result.label_url;
        window.open(result.label_url, '_blank');
        this.toastService.success('Shipping label generated successfully!');
      } else {
        this.toastService.error('Failed to generate label URL. Response: ' + JSON.stringify(result));
      }
    } catch (error: any) {
      console.error('Error generating label:', error);
      this.toastService.error('Failed to generate shipping label: ' + (error?.message || 'Unknown error'));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Generate invoice PDF (uses custom PDF export)
   */
  async generateInvoice(): Promise<void> {
    await this.exportOrder();
  }

  /**
   * Refresh shipment status from Shiprocket to get latest AWB code
   */
  async refreshShipmentStatus(): Promise<void> {
    if (!this.order?.shiprocketShipmentId) {
      this.toastService.warning('Shipment ID not found. Please create shipment first.');
      return;
    }

    try {
      this.loading = true;
      this.toastService.info('Refreshing shipment status from Shiprocket...');
      
      console.log('Refreshing shipment status for shipmentId:', this.order.shiprocketShipmentId);
      
      const result = await this.adminOrderService.refreshShipmentStatus(
        this.order.id,
        Number(this.order.shiprocketShipmentId)
      );
      
      console.log('Refresh shipment status result:', result);
      
      if (result.success) {
        // Update local order object
        if (result.awbCode) {
          this.order.shiprocketAwbCode = result.awbCode;
          this.order.trackingNumber = result.awbCode;
        }
        if (result.courierName) {
          this.order.shiprocketCourierName = result.courierName;
        }
        if (result.status) {
          // Status might have changed
        }
        
        // Reload order to get latest data
        await this.loadOrder();
        
        if (result.awbCode) {
          this.toastService.success(`AWB code updated! Tracking Number: ${result.awbCode}`);
        } else {
          this.toastService.info('AWB code not yet assigned. Shiprocket is still processing. Please try again in a few minutes.');
        }
      } else {
        this.toastService.error(result.error || 'Failed to refresh shipment status.');
      }
    } catch (error: any) {
      console.error('Error refreshing shipment status:', error);
      this.toastService.error('Failed to refresh shipment status: ' + (error?.message || 'Unknown error'));
    } finally {
      this.loading = false;
    }
  }

  /**
   * Toggle accordion section
   */
  toggleAccordion(section: string): void {
    this.accordionState[section] = !this.accordionState[section];
  }

  /**
   * Check if accordion section is open
   */
  isAccordionOpen(section: string): boolean {
    return this.accordionState[section] || false;
  }

  async trackShipment(): Promise<void> {
    console.log('trackShipment() called');
    console.log('Order AWB code:', this.order?.shiprocketAwbCode);
    
    if (!this.order?.shiprocketAwbCode) {
      this.toastService.warning('AWB code not found yet. Shiprocket will assign AWB after courier selection. Check Shiprocket dashboard to assign courier.');
      return;
    }

    try {
      this.loading = true;
      this.toastService.info('Fetching tracking information...');
      
      const result = await this.shiprocketService.trackShipment(this.order.shiprocketAwbCode).toPromise();
      
      console.log('Tracking result:', result);
      
      if (result?.tracking_data) {
        const tracking = result.tracking_data;
        
        // Store tracking data for display
        this.shiprocketTrackingData = tracking;
        
        // Update estimated delivery if available
        if (tracking.etd && !this.estimatedDeliveryDate) {
          try {
            const etdDate = new Date(tracking.etd);
            this.estimatedDeliveryDate = etdDate.toISOString().split('T')[0];
          } catch (e) {
            console.error('Error parsing ETD:', e);
          }
        }

        // Check if Shiprocket marks the order as delivered
        if (tracking.status && tracking.status.toLowerCase().includes('delivered')) {
          // Extract delivered date from Shiprocket tracking events
          let deliveredDateFromShiprocket: Date | undefined;
          
          // Find the delivered event in tracking history
          if (tracking.tracking_events && Array.isArray(tracking.tracking_events)) {
            const deliveredEvent = tracking.tracking_events.find((event: any) => 
              event.status && event.status.toLowerCase().includes('delivered')
            );
            if (deliveredEvent && deliveredEvent.scan_date_time) {
              try {
                deliveredDateFromShiprocket = new Date(deliveredEvent.scan_date_time);
              } catch (e) {
                console.error('Error parsing delivered date from Shiprocket:', e);
              }
            }
          }
          
          // Update order status to delivered if not already
          if (this.order.status !== 'delivered') {
            await this.adminOrderService.updateOrderStatus(
              this.order.id,
              'delivered',
              undefined, // notes
              deliveredDateFromShiprocket // Pass delivered date from Shiprocket
            );
            // Reload order to get updated status
            await this.loadOrder();
          } else if (!this.order.deliveredDate && deliveredDateFromShiprocket) {
            // If already delivered but no deliveredDate set, update it
            const orderRef = doc(this.firestore, 'orderTable', this.order.id);
            await updateDoc(orderRef, { deliveredDate: deliveredDateFromShiprocket });
            await this.loadOrder();
          }
        }
        
        this.toastService.success('Tracking information retrieved');
      } else {
        this.toastService.error('No tracking information available.');
      }
    } catch (error) {
      console.error('Error tracking shipment:', error);
      this.toastService.error('Failed to track shipment.');
    } finally {
      this.loading = false;
    }
  }
  
  /**
   * Load Shiprocket tracking data if shipment exists
   */
  async loadShiprocketTracking(): Promise<void> {
    if (this.order?.shiprocketAwbCode && !this.shiprocketTrackingData) {
      try {
        const result = await this.shiprocketService.trackShipment(this.order.shiprocketAwbCode).toPromise();
        if (result?.tracking_data) {
          this.shiprocketTrackingData = result.tracking_data;
          if (result.tracking_data.etd && !this.estimatedDeliveryDate) {
            try {
              const etdDate = new Date(result.tracking_data.etd);
              this.estimatedDeliveryDate = etdDate.toISOString().split('T')[0];
            } catch (e) {
              // Ignore date parsing errors
            }
          }

          // Check if Shiprocket marks the order as delivered
          if (result.tracking_data.status && result.tracking_data.status.toLowerCase().includes('delivered')) {
            // Extract delivered date from Shiprocket tracking events
            let deliveredDateFromShiprocket: Date | undefined;
            
            // Find the delivered event in tracking history
            if (result.tracking_data.tracking_events && Array.isArray(result.tracking_data.tracking_events)) {
              const deliveredEvent = result.tracking_data.tracking_events.find((event: any) => 
                event.status && event.status.toLowerCase().includes('delivered')
              );
              if (deliveredEvent && deliveredEvent.scan_date_time) {
                try {
                  deliveredDateFromShiprocket = new Date(deliveredEvent.scan_date_time);
                } catch (e) {
                  console.error('Error parsing delivered date from Shiprocket:', e);
                }
              }
            }
            
            // Update order status to delivered if not already
            if (this.order.status !== 'delivered') {
              await this.adminOrderService.updateOrderStatus(
                this.order.id,
                'delivered',
                undefined, // notes
                deliveredDateFromShiprocket // Pass delivered date from Shiprocket
              );
              // Reload order to get updated status
              await this.loadOrder();
            } else if (!this.order.deliveredDate && deliveredDateFromShiprocket) {
              // If already delivered but no deliveredDate set, update it
              const orderRef = doc(this.firestore, 'orderTable', this.order.id);
              await updateDoc(orderRef, { deliveredDate: deliveredDateFromShiprocket });
              await this.loadOrder();
            }
          }
        }
      } catch (error) {
        console.error('Error loading Shiprocket tracking:', error);
        // Don't show error to user - tracking might not be available yet
      }
    }
  }
} 